import { useQuery } from "@tanstack/react-query";
import { apiRelAnaliseQualidade } from "pages/relatorios/api/apiRelAnaliseQualidade";
import { RelatorioContainer } from "pages/relatorios/components/RelatorioContainer";
import { useRelatorioStore } from "pages/relatorios/store";
import type { ApiRelAnaliseQualidadeProdutor } from "pages/relatorios/types/ApiRelAnaliseQualidade";
import type { ApiRelatorioGetParams } from "pages/relatorios/types/ApiRelatorioParams";
import { buildRelatorioGetParams } from "pages/relatorios/utils/buildRelatorioGetParams";
import { useEffect, useMemo, useState } from "react";
import type { TExportToCSV } from "std/csv";
import { FilterBarAnaliseQualidade } from "./FilterBar";
import { PrintOptionsModal } from "./PrintOptionsModal";
import {
	defaultPrintOptions,
	loadPrintOptions,
	savePrintOptions,
	type PrintOptionsAnaliseQualidade,
} from "./printOptions";
import { produtorColumns, TableRelAnaliseQualidade } from "./TableRelAnaliseQualidade";

export function RelatorioAnaliseQualidade() {
	const filter = useRelatorioStore((s) => s.filter);
	const limparFiltros = useRelatorioStore((s) => s.limparFiltros);
	const [activeTab, setActiveTab] = useState("produtores");
	const [printModalOpen, setPrintModalOpen] = useState(false);
	const [printDraft, setPrintDraft] = useState<PrintOptionsAnaliseQualidade>(defaultPrintOptions("produtores"));
	const [printOptions, setPrintOptions] = useState<PrintOptionsAnaliseQualidade>(defaultPrintOptions("produtores"));

	const params: ApiRelatorioGetParams = useMemo(() => {
		const base = buildRelatorioGetParams({
			...filter,
			filterData: undefined,
		});
		const rotaIds = (filter?.filterRotas ?? []).map((rota) => rota.rot_idpk);

		return {
			...base,
			filterDataInicio: undefined,
			filterDataFim: undefined,
			filterRotaIdpk: rotaIds.length > 0 ? rotaIds.join(",") : undefined,
		};
	}, [filter]);

	const { data, isFetching, refetch } = useQuery({
		queryKey: ["apiRelAnaliseQualidade", params],
		queryFn: () => apiRelAnaliseQualidade({ params }),
		enabled: false,
	});

	const rotas = useMemo(() => data?.registros || [], [data]);
	const produtores = useMemo(() => data?.produtores || [], [data]);
	const qtdRotasFiltradas = filter?.filterRotas?.length ?? 0;

	const csvData: TExportToCSV<ApiRelAnaliseQualidadeProdutor> = useMemo(() => {
		return {
			type: "default",
			fileName: "relatorio-analise-qualidade",
			options: {
				columns: produtorColumns,
				data: produtores,
			},
		};
	}, [produtores]);

	useEffect(() => {
		limparFiltros();
	}, [limparFiltros]);

	useEffect(() => {
		if (!data) {
			return;
		}

		setActiveTab(qtdRotasFiltradas === 1 ? "rotas" : "produtores");
	}, [data, qtdRotasFiltradas]);

	const handleOpenPrint = (): void => {
		const saved = loadPrintOptions();
		const tabDefaults = defaultPrintOptions(activeTab);
		setPrintDraft({
			incluirResumo: saved?.incluirResumo !== false,
			modelo: tabDefaults.modelo,
		});
		setPrintModalOpen(true);
	};

	const handleConfirmPrint = (): void => {
		const tabDefaults = defaultPrintOptions(activeTab);
		const next: PrintOptionsAnaliseQualidade = {
			...printDraft,
			modelo: activeTab === "rotas" ? printDraft.modelo : tabDefaults.modelo,
		};
		savePrintOptions(next);
		setPrintOptions(next);
		setPrintModalOpen(false);
		window.setTimeout(() => {
			useRelatorioStore.setState({ isImpressaoRequisitado: true });
		}, 50);
	};

	const title = "Análise de Qualidade";

	return (
		<>
			<RelatorioContainer
				breadCrumbs={["Relatórios", "Qualidade", "Análise"]}
				refetch={refetch}
				filterBar={<FilterBarAnaliseQualidade />}
				loading={isFetching}
				title={title}
				csvData={csvData}
				registros={produtores}
				onImprimir={handleOpenPrint}
				table={
					<TableRelAnaliseQualidade
						rotas={rotas}
						produtores={produtores}
						resumo={data?.resumo}
						title={title}
						activeTab={activeTab}
						onTabChange={setActiveTab}
						printOptions={printOptions}
					/>
				}
			/>

			<PrintOptionsModal
				open={printModalOpen}
				value={printDraft}
				onChange={setPrintDraft}
				onCancel={() => setPrintModalOpen(false)}
				onConfirm={handleConfirmPrint}
			/>
		</>
	);
}
